import React from 'react';
import { toast } from 'react-toastify';
import {
    updateCompanyLogo,
    updateCompanyName,
    updateCompanySettings,
} from '../../../../../actions/company';
import {
    getCompanyData,
    resolveCompanyLogoData,
    updateCompanyData,
} from '../../../../../helpers/company-helper';
import { toAbsoluteApiUrl } from '../../../../../helpers/http-client';
import { deleteFileAsset, detachFileReference, uploadFileAsset } from '../../../../../helpers/file-helper';

const DEFAULT_TIMEZONE = 'America/Mexico_City';
const MAX_LOGO_SIZE_BYTES = 2 * 1024 * 1024;
const ALLOWED_LOGO_TYPES = ['image/png', 'image/jpeg', 'image/svg+xml'];

const EMPTY_FORM = {
    name: '',
    description: '',
    website: '',
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    timezone: DEFAULT_TIMEZONE,
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const normalizeFormData = (formData = {}) => ({
    name: String(formData.name || '').trim(),
    description: String(formData.description || '').trim(),
    website: String(formData.website || '').trim(),
    contactName: String(formData.contactName || '').trim(),
    contactEmail: String(formData.contactEmail || '').trim(),
    contactPhone: String(formData.contactPhone || '').trim(),
    timezone: String(formData.timezone || DEFAULT_TIMEZONE).trim() || DEFAULT_TIMEZONE,
});

const buildFormDataFromCompany = (company = {}) => {
    const settings = company?.settings && typeof company.settings === 'object' ? company.settings : {};

    return {
        name: String(company?.name || ''),
        description: String(settings.description || ''),
        website: String(settings.website || ''),
        contactName: String(settings.contactName || ''),
        contactEmail: String(settings.contactEmail || ''),
        contactPhone: String(settings.contactPhone || ''),
        timezone: String(settings.timezone || DEFAULT_TIMEZONE) || DEFAULT_TIMEZONE,
    };
};

const formatTimezoneLabel = (timeZone = '') => {
    const parts = String(timeZone || '').split('/');
    const cityPart = parts.slice(1).join(' / ') || parts[0] || timeZone;
    return cityPart.replace(/_/g, ' ');
};

const buildTimeZoneOptions = () => {
    if (typeof Intl.supportedValuesOf !== 'function') {
        return [
            {
                value: DEFAULT_TIMEZONE,
                label: formatTimezoneLabel(DEFAULT_TIMEZONE),
                meta: DEFAULT_TIMEZONE,
                group: 'America',
            },
        ];
    }

    return Intl.supportedValuesOf('timeZone').map((timeZone) => ({
        value: timeZone,
        label: formatTimezoneLabel(timeZone),
        meta: timeZone,
        group: String(timeZone).split('/')[0] || 'Otros',
    }));
};

const formatFileSize = (sizeBytes = 0) => {
    if (!sizeBytes) {
        return '0 KB';
    }

    if (sizeBytes < 1024 * 1024) {
        return `${(sizeBytes / 1024).toFixed(1)} KB`;
    }

    return `${(sizeBytes / (1024 * 1024)).toFixed(2)} MB`;
};

export const useAccountGeneralTab = ({ token, companyId, dispatch }) => {
    const [formData, setFormData] = React.useState(EMPTY_FORM);
    const [logoFile, setLogoFile] = React.useState(null);
    const [logoPreview, setLogoPreview] = React.useState(null);
    const [currentLogoUrl, setCurrentLogoUrl] = React.useState(null);
    const [currentLogoName, setCurrentLogoName] = React.useState('');
    const [currentLogoSizeBytes, setCurrentLogoSizeBytes] = React.useState(0);
    const [loading, setLoading] = React.useState(true);
    const [saving, setSaving] = React.useState(false);
    const [error, setError] = React.useState(null);
    const [logoRemoved, setLogoRemoved] = React.useState(false);
    const [persistedLogoFileId, setPersistedLogoFileId] = React.useState('');

    const initialFormSnapshotRef = React.useRef(normalizeFormData(EMPTY_FORM));
    const logoInputRef = React.useRef(null);
    const timezoneOptions = React.useMemo(() => buildTimeZoneOptions(), []);

    React.useEffect(() => {
        return () => {
            if (logoPreview) {
                URL.revokeObjectURL(logoPreview);
            }
        };
    }, [logoPreview]);

    React.useEffect(() => {
        const loadCompany = async () => {
            if (!token || !companyId) {
                setLoading(false);
                setError('No se pudieron obtener los datos de la empresa.');
                return;
            }

            setLoading(true);
            setError(null);

            const resp = await getCompanyData(token, { companyId });

            if (!resp?.success || !resp?.data) {
                setError(resp?.message || 'No se pudieron obtener los datos de la empresa.');
                setLoading(false);
                return;
            }

            const company = resp.data;
            const nextFormData = buildFormDataFromCompany(company);
            const nextLogoData = resolveCompanyLogoData(company);

            setFormData(nextFormData);
            setCurrentLogoUrl(nextLogoData.logoUrl || null);
            setCurrentLogoName(nextLogoData.logoName);
            setCurrentLogoSizeBytes(nextLogoData.logoSizeBytes);
            setPersistedLogoFileId(nextLogoData.logoFileId);
            setLogoRemoved(false);
            initialFormSnapshotRef.current = normalizeFormData(nextFormData);

            dispatch(updateCompanyName(String(company.name || '')));
            dispatch(updateCompanySettings(company.settings || {}));
            dispatch(updateCompanyLogo(nextLogoData.logoUrl || ''));

            setLoading(false);
        };

        loadCompany();
    }, [companyId, dispatch, token]);

    const activeLogoUrl = logoPreview || currentLogoUrl || '';

    const selectedLogoSummary = React.useMemo(() => {
        if (logoFile) {
            return `${logoFile.name} · ${formatFileSize(logoFile.size)}`;
        }

        if (currentLogoName) {
            return `${currentLogoName} · ${formatFileSize(currentLogoSizeBytes)}`;
        }

        return '';
    }, [currentLogoName, currentLogoSizeBytes, logoFile]);

    const isDirty = React.useMemo(() => {
        const normalizedCurrent = normalizeFormData(formData);
        const normalizedInitial = initialFormSnapshotRef.current;

        return JSON.stringify(normalizedCurrent) !== JSON.stringify(normalizedInitial)
            || Boolean(logoFile)
            || Boolean(logoRemoved);
    }, [formData, logoFile, logoRemoved]);

    const handleFieldChange = React.useCallback((event) => {
        const { name, value } = event.target;
        setFormData((previous) => ({
            ...previous,
            [name]: value,
        }));
    }, []);

    const handleTimezoneChange = React.useCallback((value) => {
        setFormData((previous) => ({
            ...previous,
            timezone: value || DEFAULT_TIMEZONE,
        }));
    }, []);

    const handleOpenLogoPicker = React.useCallback(() => {
        if (saving) {
            return;
        }

        logoInputRef.current?.click();
    }, [saving]);

    const handleRemoveLogo = React.useCallback(() => {
        if (logoPreview) {
            URL.revokeObjectURL(logoPreview);
        }

        setLogoPreview(null);
        setLogoFile(null);
        setCurrentLogoUrl(null);
        setCurrentLogoName('');
        setCurrentLogoSizeBytes(0);
        setLogoRemoved(true);
        setError(null);

        if (logoInputRef.current) {
            logoInputRef.current.value = '';
        }
    }, [logoPreview]);

    const handleLogoFileChange = React.useCallback((event) => {
        const nextFile = event.target.files?.[0];

        if (!nextFile) {
            return;
        }

        if (!ALLOWED_LOGO_TYPES.includes(nextFile.type)) {
            setError('El logo debe ser PNG, JPG o SVG.');
            event.target.value = '';
            return;
        }

        if (nextFile.size > MAX_LOGO_SIZE_BYTES) {
            setError('El logo no puede ser mayor a 2 MB.');
            event.target.value = '';
            return;
        }

        if (logoPreview) {
            URL.revokeObjectURL(logoPreview);
        }

        setLogoFile(nextFile);
        setLogoPreview(URL.createObjectURL(nextFile));
        setLogoRemoved(false);
        setError(null);
    }, [logoPreview]);

    const validateForm = React.useCallback(() => {
        const normalized = normalizeFormData(formData);

        if (!normalized.name) {
            return 'El nombre de la empresa es obligatorio.';
        }

        if (normalized.name.length < 2) {
            return 'El nombre debe tener al menos 2 caracteres.';
        }

        if (normalized.website) {
            try {
                const url = new URL(normalized.website);
                if (!['http:', 'https:'].includes(url.protocol)) {
                    return 'El sitio web debe ser una URL válida.';
                }
            } catch (_error) {
                return 'El sitio web debe ser una URL válida.';
            }
        }

        if (normalized.contactEmail && !EMAIL_PATTERN.test(normalized.contactEmail)) {
            return 'El email de contacto no es válido.';
        }

        if (!normalized.timezone) {
            return 'La zona horaria es obligatoria.';
        }

        return '';
    }, [formData]);

    const handleSubmit = React.useCallback(async () => {
        if (!token || !companyId || saving) {
            return;
        }

        const validationError = validateForm();
        if (validationError) {
            setError(validationError);
            return;
        }

        setSaving(true);
        setError(null);

        const normalized = normalizeFormData(formData);
        let uploadedFileId = '';
        let nextLogoUrl = currentLogoUrl || '';

        if (logoFile) {
            const uploadResp = await uploadFileAsset(token, {
                file: logoFile,
                purpose: 'COMPANY_LOGO',
                tags: ['company', 'logo'],
                metadata: {
                    source: 'account_settings_general',
                },
                referenceType: 'COMPANY',
                referenceId: String(companyId || ''),
                referenceField: 'logo',
                referenceLabel: normalized.name || 'Logo de empresa',
                isPublic: true,
            });

            uploadedFileId = String(uploadResp?.data?._id || '');
            nextLogoUrl = toAbsoluteApiUrl(uploadResp?.data?.url || '') || nextLogoUrl;

            if (!uploadResp?.success || !uploadedFileId) {
                setError(uploadResp?.message || 'No se pudo subir el logo de la empresa.');
                setSaving(false);
                return;
            }
        }

        const payload = {
            name: normalized.name,
            settings: {
                description: normalized.description,
                website: normalized.website,
                contactName: normalized.contactName,
                contactEmail: normalized.contactEmail,
                contactPhone: normalized.contactPhone,
                timezone: normalized.timezone,
            },
        };

        if (uploadedFileId) {
            payload.settings.logoFileId = uploadedFileId;
        } else if (logoRemoved) {
            payload.settings.logoFileId = null;
        }

        const previousLogoFileId = persistedLogoFileId;
        const saveResp = await updateCompanyData(token, { companyId, payload });

        if (!saveResp?.success || !saveResp?.data) {
            if (uploadedFileId) {
                await deleteFileAsset(token, uploadedFileId);
            }

            setError(saveResp?.message || 'No se pudo guardar la configuración de la empresa.');
            setSaving(false);
            return;
        }

        const updatedCompany = saveResp.data;
        const nextFormData = buildFormDataFromCompany(updatedCompany);
        const nextLogoData = resolveCompanyLogoData(updatedCompany);
        const nextLogoFileId = nextLogoData.logoFileId;

        if (previousLogoFileId && previousLogoFileId !== nextLogoFileId) {
            await detachFileReference(token, previousLogoFileId, {
                referenceType: 'COMPANY',
                referenceId: String(companyId || ''),
                referenceField: 'logo',
                deleteIfOrphan: true,
            });
        }

        if (logoPreview) {
            URL.revokeObjectURL(logoPreview);
        }

        setFormData(nextFormData);
        setLogoFile(null);
        setLogoPreview(null);
        setCurrentLogoUrl(nextLogoData.logoUrl || nextLogoUrl || null);
        setCurrentLogoName(nextLogoData.logoName);
        setCurrentLogoSizeBytes(nextLogoData.logoSizeBytes);
        setPersistedLogoFileId(nextLogoFileId);
        setLogoRemoved(false);
        initialFormSnapshotRef.current = normalizeFormData(nextFormData);

        dispatch(updateCompanyName(String(updatedCompany.name || normalized.name)));
        dispatch(updateCompanySettings(updatedCompany.settings || {}));
        dispatch(updateCompanyLogo(nextLogoData.logoUrl || nextLogoUrl || ''));

        toast.success(saveResp?.message || 'Configuración guardada correctamente.');
        setSaving(false);

        if (logoInputRef.current) {
            logoInputRef.current.value = '';
        }
    }, [
        companyId,
        currentLogoUrl,
        dispatch,
        formData,
        logoFile,
        logoPreview,
        logoRemoved,
        persistedLogoFileId,
        saving,
        token,
        validateForm,
    ]);

    return {
        formData,
        logoFile,
        logoPreview,
        currentLogoUrl,
        loading,
        saving,
        error,
        isDirty,
        activeLogoUrl,
        selectedLogoSummary,
        logoInputRef,
        timezoneOptions,
        handleFieldChange,
        handleTimezoneChange,
        handleOpenLogoPicker,
        handleLogoFileChange,
        handleRemoveLogo,
        handleSubmit,
    };
};
